import AssessmentUxToggle from "./AssessmentUxToggle";

/**
 * Shared top nav for Conversational + Survey form participant UX.
 */
export default function AssessmentSharedNav({
  T,
  companyName,
  productName = null,
  productLogoUrl = null,
  modeBadge = "Conversational",
  uxMode,
  onUxModeChange,
  progressPct = null,
  questionsLeft = null,
  showProgress = false,
  voiceEnabled = null,
  onToggleVoice = null,
  ttsSupported = false,
  onToggleTheme,
  useDefaultProductLogo = true,
}) {
  const dark = T.dark;
  const logoSrc = productLogoUrl || (useDefaultProductLogo ? "/5x-logo-tagline.png" : null);
  const brandLabel = productName || "Assessment";
  return (
    <nav
      className="lga-nav"
      style={{
        position: "relative",
        zIndex: 40,
        flexShrink: 0,
        background: T.nav,
        borderBottom: `1px solid ${T.navBorder}`,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
    >
      <div className="lga-nav-inner" style={{ maxWidth: 960, margin: "0 auto" }}>
        <div className="lga-nav-brand">
          {productLogoUrl ? (
            <img src={logoSrc} alt={brandLabel} className="lga-nav-logo" />
          ) : productName ? (
            <span
              className="lga-nav-product-name"
              style={{
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: dark ? "#fff" : T.brand || "#0f172a",
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {brandLabel}
            </span>
          ) : logoSrc ? (
            <img src={logoSrc} alt={brandLabel} className="lga-nav-logo" />
          ) : (
            <span
              className="lga-nav-product-name"
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: dark ? "#fff" : T.brand || "#0f172a",
              }}
            >
              {brandLabel}
            </span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {companyName ? (
              <span className="lga-nav-company" style={{ color: T.text2 }}>
                {companyName}
              </span>
            ) : null}
            <span
              className="lga-nav-badge"
              style={{
                alignItems: "center",
                gap: 4,
                borderRadius: 999,
                padding: "3px 10px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                background: T.pillarBg,
                border: `1px solid ${T.pillarBorder}`,
                color: T.pillarColor,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: T.pillarColor,
                  display: "inline-block",
                  animation: "lga-pulse-dot 1.8s ease-in-out infinite",
                }}
              />
              {modeBadge}
            </span>
          </div>
        </div>

        <div className="lga-nav-controls">
          {onUxModeChange ? (
            <AssessmentUxToggle
              mode={uxMode}
              onChange={onUxModeChange}
              dark={dark}
              compact
            />
          ) : null}

          {showProgress ? (
            <div
              className="lga-nav-progress-detail"
              style={{ textAlign: "right", marginRight: 4 }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text1 }}>
                {progressPct ?? 0}%
              </div>
              <div style={{ fontSize: 10, color: T.text3 }}>
                {Math.max(0, questionsLeft ?? 0)} left
              </div>
            </div>
          ) : null}

          {ttsSupported && onToggleVoice ? (
            <button
              type="button"
              onClick={onToggleVoice}
              title={
                voiceEnabled
                  ? "AI voice on — click to mute"
                  : "AI voice off — click to enable"
              }
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s",
                background: voiceEnabled
                  ? dark
                    ? "rgba(0,55,80,0.85)"
                    : "#003d5c"
                  : dark
                    ? "rgba(56,189,248,0.14)"
                    : "rgba(0,79,115,0.08)",
                border: voiceEnabled
                  ? dark
                    ? "1.5px solid rgba(0,79,115,0.9)"
                    : "1.5px solid #002a40"
                  : dark
                    ? "1.5px solid rgba(56,189,248,0.55)"
                    : "1.5px solid rgba(0,79,115,0.35)",
              }}
            >
              {voiceEnabled ? (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={dark ? "#67e8f9" : "#fff"}
                  strokeWidth={2}
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path
                    strokeLinecap="round"
                    d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14"
                  />
                </svg>
              ) : (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={dark ? "#38bdf8" : "#004f73"}
                  strokeWidth={2}
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              )}
            </button>
          ) : null}

          <button
            type="button"
            onClick={onToggleTheme}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              borderRadius: 999,
              padding: "5px 9px",
              fontSize: 10,
              fontWeight: 600,
              cursor: "pointer",
              background: dark
                ? "rgba(255,255,255,0.07)"
                : "rgba(0,79,115,0.06)",
              border: dark
                ? "1px solid rgba(255,255,255,0.10)"
                : "1px solid rgba(0,79,115,0.14)",
              color: dark
                ? "rgba(255,255,255,0.50)"
                : "rgba(0,79,115,0.60)",
            }}
          >
            {dark ? (
              <svg
                width="11"
                height="11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            ) : (
              <svg
                width="11"
                height="11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                />
              </svg>
            )}
            <span className="lga-theme-label">{dark ? "Light" : "Dark"}</span>
          </button>
        </div>
      </div>

      {showProgress ? (
        <div
          style={{
            height: 2,
            background: dark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,79,115,0.07)",
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progressPct ?? 0}%`,
              borderRadius: "0 2px 2px 0",
              background: "linear-gradient(90deg,#004f73,#1a7fa8,#004f73)",
              backgroundSize: "200% auto",
              animation: "lga-progress 2.5s linear infinite",
              transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </div>
      ) : null}
    </nav>
  );
}
